import { useCallback } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { DeepPartial } from '../../../temporary/deepPartial';
import { useStoreApi } from '../../api';
import { storeKeys } from '../../query-key-factory';
import { Cart, CartItem } from '../cart.types';

const CART_ITEM_UPDATE_QUERY_KEY = ['cart', 'item', 'update'];

type UpdateCartResponse = {
  cart: Cart;
  cart_item: CartItem;
};
interface CartItemUpdate extends DeepPartial<CartItem> {}

/** Item Update Request base, doesn't include cart fields at root level  */
type UpdateCartItemRequestBase = {
  cartItemId: string;
  cartId: string;
  cart_item?: CartItemUpdate;
  /**
   * The Id you want to invalidate
   * When using on management side, this is required
   */
  scopeId?: string;

  /**
   * Only required when a cart is purchased
   */
  cause_id?: string;
};

type OnCartSuccess<TRequest extends UpdateCartItemRequest> = (
  data: UpdateCartResponse,
  variables: TRequest,
  context: unknown
) => unknown;

const useOnCartSuccess = <TRequest extends UpdateCartItemRequest>(): OnCartSuccess<TRequest> => {
  const queryClient = useQueryClient();
  return (data, variables) => {
    const { scopeId = 'user' } = variables;
    const cartId = data.cart.uuid;
    // We can tie an update to either the causeId, or 'user' if we don't have a causeId
    const invalidationKey = storeKeys.id(scopeId).carts.id(cartId).detail;
    void queryClient.invalidateQueries({ queryKey: storeKeys.id('user').consumer.checkout.all });
    return void queryClient.invalidateQueries({ queryKey: invalidationKey });
  };
};

/**
 * Update Cart Requests,
 * Included cart Item fields at root level
 * get properly attached to the cart_item field
 * */

interface UpdateCartItemRequest extends UpdateCartItemRequestBase, CartItemUpdate {}

type UseCartItemUpdate<TRequest extends UpdateCartItemRequest> = (
  request: TRequest
) => UpdateCartItemRequest;

const useCartItemUpdate = <TRequest extends UpdateCartItemRequest>(
  shapeRequest?: UseCartItemUpdate<TRequest>
) => {
  const StoreApi = useStoreApi();
  const onSuccess = useOnCartSuccess<TRequest>();
  const updateCartItemBase = useCallback(
    async ({ scopeId, ...updateRequest }: UpdateCartItemRequest) => {
      const { cartId, cartItemId, cart_item, cause_id, ...rest } = updateRequest;
      const { data } = await StoreApi.put<UpdateCartResponse>(
        `/carts/${cartId}/items/${cartItemId}`,
        { cause_id, cart_item: { ...cart_item, ...rest } }
      );

      return data;
    },
    []
  );

  const updateCartItem = useCallback(
    /** If a shape was passed in, format it the response correctly */
    async (request: TRequest) => await updateCartItemBase(shapeRequest?.(request) ?? request),
    []
  );

  return {
    onSuccess,
    updateCartItem,
  };
};

interface UseUpdateCartItem extends UpdateCartItemRequestBase {}

/** Updates a nested cart Item */
const useUpdateCartItem = () => {
  const { updateCartItem, onSuccess } = useCartItemUpdate<UseUpdateCartItem>();

  return useMutation({
    mutationFn: updateCartItem,
    onSuccess,
  });
};

interface UseUpdateCartItemPrice extends Omit<UpdateCartItemRequest, 'cart_item'> {
  price: number;
  designation?: string;
  maximum?: number;
}

/** Updates a nested cart Items Price */
const useUpdateCartItemPrice = () => {
  const { updateCartItem, onSuccess } = useCartItemUpdate<UseUpdateCartItemPrice>((request) => ({
    ...request,
    cart_item: {
      price: request.price,
      designation: request?.designation,
      maximum: request?.maximum,
    },
  }));

  return useMutation({
    mutationFn: updateCartItem,
    onSuccess,
  });
};

interface UseUpdateCartItemQuantity extends Omit<UpdateCartItemRequest, 'cart_item'> {
  quantity: number;
}

/** Updates a nested cart Items Quantity */
const useUpdateCartItemQuantity = () => {
  const { updateCartItem, onSuccess } = useCartItemUpdate<UseUpdateCartItemQuantity>((request) => ({
    ...request,
    cart_item: { quantity: request.quantity },
  }));

  return useMutation({
    mutationFn: updateCartItem,
    onSuccess,
    mutationKey: CART_ITEM_UPDATE_QUERY_KEY,
  });
};

export { useUpdateCartItem, useUpdateCartItemPrice, useUpdateCartItemQuantity };
